import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2, Loader2, X, ShieldAlert } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

// Phase TRASH-2: finance entity types added alongside the Phase-1 set.
// Finance rows render a dual-message hint in the confirm dialog so the
// operator sees at a glance whether the row will be hard-purged (draft /
// demo) or archived forever (confirmed / real).
type EntityType =
    | "lead" | "person" | "program" | "group" | "room"
    | "invoice" | "payment" | "expense" | "payroll_run" | "other_income";

const ENTITY_PATH: Record<EntityType, string> = {
    lead: "leads", person: "people", program: "programs",
    group: "groups", room: "rooms",
    invoice: "invoices", payment: "payments",
    expense: "cash/expenses",  // sits under the cash router
    payroll_run: "payroll/runs",
    other_income: "finance/other-income",
};

const FINANCE_TYPES = new Set<EntityType>([
    "invoice", "payment", "expense", "payroll_run", "other_income",
]);

type PreviewResp = {
    entity: { id: string; type: string; summary: Record<string, unknown>; already_archived?: boolean } | null;
    dependents: Record<string, { count: number; samples: Array<{ id: string; label: string }> }>;
    blocker: string | null;
    // Phase TRASH-2: preview does not return this yet, but delete does —
    // we compute optimistically from the entity's own status when we
    // have it, so the dialog can display the correct hint pre-submit.
};

interface Props {
    entityType: EntityType;
    entityId: string;
    entityLabel: string;
    onArchived?: () => void;
    variant?: "icon" | "button";
    stopPropagation?: boolean;
    dataTestidPrefix?: string;
    // Phase TRASH-2: caller passes the row's status so the dialog can
    // show whether hard-purge applies without a second round-trip.
    // Anything other than "draft" is treated as archive-only for finance
    // rows; ignored for non-finance types.
    entityStatus?: string;
    // Phase TD-2: when a platform owner opens the Platforma global lists
    // (People/Groups/Programs/…) each row carries its own tenant_id. The
    // delete + preview endpoints are tenant-scoped, so we pin the write
    // via `X-Target-Tenant-Id`. Omitting this prop keeps the old
    // tenant-scoped behaviour for institution owners.
    targetTenantId?: string;
}

export function TrashDeleteAction({
    entityType, entityId, entityLabel, onArchived,
    variant = "icon", stopPropagation = true,
    dataTestidPrefix, entityStatus,
    targetTenantId,
}: Props) {
    const { t, i18n } = useTranslation();
    const { hasPerm } = useAuth();
    const [open, setOpen] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [preview, setPreview] = useState<PreviewResp | null>(null);

    // Only Institution Owner / Platform Owner may see this.
    if (!hasPerm("trash.manage")) return null;

    // Phase TD-2: pin cross-tenant deletes to the row's tenant (header +
    // query fallback in case a proxy strips custom headers).
    const targetHeaders = targetTenantId ? { "X-Target-Tenant-Id": targetTenantId } : undefined;
    const targetParams = targetTenantId ? { target_tenant_id: targetTenantId } : undefined;

    const openDialog = async (ev: React.MouseEvent) => {
        if (stopPropagation) ev.stopPropagation();
        setOpen(true);
        setLoadingPreview(true);
        setPreview(null);
        try {
            const r = await api.get(`/${ENTITY_PATH[entityType]}/${entityId}/dependency-preview`, { headers: targetHeaders, params: targetParams });
            setPreview(r.data);
        } catch (e: any) {
            toast.error(formatApiError(e) || t("trash.delete_failed"));
            setOpen(false);
        } finally {
            setLoadingPreview(false);
        }
    };

    const doDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/${ENTITY_PATH[entityType]}/${entityId}`, { headers: targetHeaders, params: targetParams });
            toast.success(t("trash.deleted"));
            setOpen(false);
            onArchived?.();
        } catch (e: any) {
            const err = e?.response?.data?.detail?.error || e?.response?.data?.error;
            if (typeof err === "string" && i18n.exists(`trash.delete_blocker.${err}`)) {
                toast.error(t(`trash.delete_blocker.${err}`));
            } else {
                toast.error(`${t("trash.delete_failed")} ${formatApiError(e) || ""}`.trim());
            }
        } finally {
            setDeleting(false);
        }
    };

    const testid = dataTestidPrefix || `trash-delete-${entityType}-${entityId}`;

    const totalDependents = preview
        ? Object.values(preview.dependents || {}).reduce((s, x) => s + (x?.count || 0), 0)
        : 0;

    const blockerKey = preview?.blocker ? `trash.delete_blocker.${preview.blocker}` : null;

    // Phase TRASH-2: finance rows show the retention rule inline so
    // Institution Owners never have to guess whether the record will
    // eventually disappear. Payroll is *always* archive-only; the rest
    // depend on the entity's status.
    const isFinance = FINANCE_TYPES.has(entityType);
    const isHardPurgeEligible = isFinance
        ? (entityType === "payroll_run" ? false : entityStatus === "draft")
        : true;
    const financeHintKey = isFinance
        ? (isHardPurgeEligible ? "trash.hard_purge_hint" : "trash.archive_only_hint")
        : null;

    return (
        <>
            {variant === "icon" ? (
                <button
                    type="button"
                    onClick={openDialog}
                    data-testid={testid}
                    title={t("trash.delete")}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-[var(--input)] transition-colors"
                    style={{ color: "var(--text-muted)" }}
                >
                    <Trash2 size={16} />
                </button>
            ) : (
                <button
                    type="button"
                    onClick={openDialog}
                    data-testid={testid}
                    className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm border transition-colors"
                    style={{ borderColor: "var(--border)", color: "var(--text)" }}
                >
                    <Trash2 size={14} /> {t("trash.delete")}
                </button>
            )}

            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
                    onClick={(e) => { if (!deleting) { e.stopPropagation(); setOpen(false); } }}
                    data-testid={`${testid}-dialog`}
                >
                    <div className="surface-card w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>
                                {t("trash.delete_confirm_title")}
                            </h2>
                            <button onClick={() => setOpen(false)} style={{ color: "var(--text-muted)" }}
                                data-testid={`${testid}-close`} disabled={deleting}
                            ><X size={18} /></button>
                        </div>

                        {loadingPreview && (
                            <div className="flex justify-center py-8">
                                <Loader2 className="animate-spin" style={{ color: "var(--text-muted)" }} />
                            </div>
                        )}

                        {!loadingPreview && preview && (
                            <div className="space-y-4">
                                <p className="text-sm" style={{ color: "var(--text)" }}>
                                    {t(isFinance ? "finance.trash.dialog_body" : "trash.delete_confirm_body", { name: entityLabel })}
                                </p>

                                {financeHintKey && (
                                    <div className="rounded-md p-3 text-sm flex items-start gap-2"
                                        data-testid={`${testid}-finance-hint`}
                                        style={{
                                            background: isHardPurgeEligible ? "var(--warning-bg)" : "var(--info-bg, var(--input))",
                                            color: isHardPurgeEligible ? "var(--warning-fg)" : "var(--info-fg, var(--text))",
                                        }}>
                                        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                                        <span>{t(financeHintKey)}</span>
                                    </div>
                                )}

                                {blockerKey && (
                                    <div className="rounded-md p-3 text-sm"
                                        style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}
                                        data-testid={`${testid}-blocker`}
                                    >{t(blockerKey, t("trash.delete_blocker.generic"))}</div>
                                )}

                                {totalDependents > 0 && !blockerKey && (
                                    <div className="rounded-md p-3 text-sm"
                                        style={{ background: "var(--input)", color: "var(--text)" }}
                                        data-testid={`${testid}-dependents`}
                                    >
                                        <div className="font-medium mb-2">
                                            {t(isFinance ? "finance.trash.dependents_title" : "trash.delete_confirm_dependents", { count: totalDependents })}
                                        </div>
                                        <ul className="space-y-1 text-xs" style={{ color: "var(--text-muted)" }}>
                                            {Object.entries(preview.dependents).map(([coll, info]) => (
                                                <li key={coll}>
                                                    <b>{t(`trash.entity.${coll}`, coll)}:</b> {info.count}
                                                    {info.samples.length > 0 && (
                                                        <span> — {info.samples.slice(0, 3).map(s => s.label).join(", ")}
                                                            {info.count > info.samples.length && "…"}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="flex justify-end gap-3 pt-2">
                                    <button type="button"
                                        className="px-4 py-2 rounded-md border text-sm"
                                        style={{ borderColor: "var(--border)", color: "var(--text)" }}
                                        onClick={() => setOpen(false)}
                                        data-testid={`${testid}-cancel`}
                                        disabled={deleting}
                                    >{t("common.cancel")}</button>
                                    <button type="button"
                                        className="px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                                        style={{ background: "var(--error-fg)", color: "#fff" }}
                                        onClick={doDelete}
                                        data-testid={`${testid}-confirm`}
                                        disabled={deleting || !!blockerKey}
                                    >
                                        {deleting && <Loader2 className="animate-spin" size={14} />}
                                        {t(isFinance ? "finance.trash.confirm" : "trash.delete")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
